import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewEncapsulation,
} from '@angular/core';
import { Router } from '@angular/router';
import { faBooks } from '@fortawesome/pro-solid-svg-icons';
import { select, Store, } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { loadMoreSingleShowcase } from 'search/full-card/actions/showcases.actions';
import {
  defaultShowcaseDisplayOrder,
  ExtractedShowcase,
  FrcShowcase,
  FrcShowcaseSource,
  FrcShowcaseType,
  ShowcaseDisplay,
  SHOWCASES_PARAMS,
  ShowcasesEntity,
  ShowcasesTypes,
} from 'search/full-card/models/showcases';
import { selectContributorsLoading, selectLoading, ShowcasesState, } from 'search/full-card/reducers/showcases.reducer';
import { ConfigurationLoader } from 'shared/configuration-loader';
import { DisplaySettings } from 'shared/models/configuration';
import { IlsType } from 'shared/models/ilsType';
import { DiscoverV2BffService } from '../../../../../../elements-v2/service/discover-v2-bff.service';
import { EntityTypes, isFormatGroup } from '../../../../../../entity/models/entity';
import { FeatureToggleService } from '../../../../../../services/feature-toggle.service';
import { InsertWidgetsStatus, SyndeticsUnboundService, SyndeticsWidget } from '../../../../../../services/syndetics-unbound.service';

@Component({
  selector: 'app-showcase-page',
  templateUrl: './showcase-page.component.html',
  styleUrls: ['./showcase-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class ShowcasePageComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input() public showcases: ExtractedShowcase[];
  @Input() public entity: ShowcasesEntity;
  public isShowcasesLoading: boolean;
  public isContributorsLoading: boolean;
  public subscriptions = new Subscription();
  public showcasesTypes = ShowcasesTypes;

  public isbn: string;
  public entityTypes = EntityTypes;
  public syndeticsWidget = SyndeticsWidget;
  public insertWidgetsStatus: InsertWidgetsStatus = {};
  public someWidgetsLoaded = false;
  public isWidgetsLoading = true;
  public displaySettings: DisplaySettings;
  public shouldLoadWidgets: boolean;
  public readonly booksIcon = faBooks;
  private collectionList: string[]; // collection list for syndetics
  private locationList: string[]; // location list for syndetics

  public frcShowcases: FrcShowcase[];
  public frcShowcasesMap: Map<FrcShowcaseType, ExtractedShowcase> = new Map();
  public frcShowcasesOrder: ShowcaseDisplay[] = defaultShowcaseDisplayOrder;

  private readonly widgetsUsed: SyndeticsWidget[] = [
    SyndeticsWidget.series,
    SyndeticsWidget.patronreviews,
    SyndeticsWidget.similar,
    SyndeticsWidget.reviews,
    SyndeticsWidget.bookprofile,
    SyndeticsWidget.readinglevel,
    SyndeticsWidget.awards,
    SyndeticsWidget.author,
  ];
  private featureSyndeticIntegrations = false;

  constructor(
    private readonly store: Store<ShowcasesState>,
    private readonly cdRef: ChangeDetectorRef,
    private readonly renderer: Renderer2,
    private readonly router: Router,
    private readonly configLoader: ConfigurationLoader,
    private readonly syndeticsUnboundService: SyndeticsUnboundService,
    private readonly featureToggleService: FeatureToggleService,
    private discoverV2BffService: DiscoverV2BffService,
  ) {
  }

  public ngOnChanges() {
    this.showcases.forEach((showcase) => this.frcShowcasesMap.set(showcase.type, showcase));

    this.frcShowcases = this.frcShowcasesOrder
    .map((showcase) => ({...showcase, showcase: this.frcShowcasesMap.get(showcase.type)}))
    .filter((showcase) => (showcase.source === FrcShowcaseSource.syndeticsUnbound || showcase.showcase?.totalCount > 0));
  }

  public ngOnInit() {
    this.isbn = this.entity.isbn;
    this.featureSyndeticIntegrations = this.featureToggleService.getToggles()['DIS-28885_2024-06-31_syndetics_integration'];

    if (this.featureSyndeticIntegrations) {
      this.subscriptions.add(this.discoverV2BffService.$page.subscribe((page) => {
        if (page.settings?.collection_list) {
          this.collectionList = page.settings.collection_list;
        }

        if (page.settings?.location_list) {
          this.locationList = page.settings.location_list;
        }

        this.cdRef.markForCheck();
      }));
    }

    this.subscriptions.add(this.store.pipe(select(selectLoading)).subscribe((isLoading: boolean) => {
      this.isShowcasesLoading = isLoading;
      this.cdRef.markForCheck();
    }));

    this.subscriptions.add(this.store.pipe(select(selectContributorsLoading)).subscribe((isContributorsLoading) => {
      this.isContributorsLoading = isContributorsLoading;
      this.cdRef.markForCheck();
    }));
  }

  public ngAfterViewInit(): void {
    const connectionConfig = this.configLoader.syndeticsUnboundConnection;
    this.displaySettings = connectionConfig?.displaySettings || {};
    this.shouldLoadWidgets =
      isFormatGroup(this.entity) &&
      connectionConfig?.accountId &&
      Object.values(this.displaySettings).some((value) => value === true);

    const list = this.configLoader.ilsType === IlsType.sierra ? this.locationList : this.collectionList;

    if (this.shouldLoadWidgets) {
      this.syndeticsUnboundService.insertWidgets(
        this.isbn,
        this.widgetsUsed,
        connectionConfig?.accountId,
        this.renderer,
        this.entity.title,
        list)
      .then((widgetsLoaded) => {
        this.insertWidgetsStatus = widgetsLoaded;
        this.someWidgetsLoaded = Object.values(widgetsLoaded).some((widgetLoaded) => widgetLoaded);
        this.isWidgetsLoading = false;
        this.cdRef.detectChanges();
      });
    } else {
      this.isWidgetsLoading = false;
      this.cdRef.detectChanges();
    }
  }

  public loadMore(identifier: string) {
    this.store.dispatch(loadMoreSingleShowcase({
      id: this.entity.id,
      entityType: this.entity.entityType,
      showcase: ShowcasesTypes.CONTRIBUTORS,
      identifier,
      pageSize: SHOWCASES_PARAMS.CONTRIBUTORS_CAROUSEL,
    }));
  }

  public trackByShowcaseType(index: number, elem: any) {
    return elem.type;
  }

  public ngOnDestroy() {
    this.subscriptions.unsubscribe();
    if (this.shouldLoadWidgets) {
      this.syndeticsUnboundService.removeWidgets(this.widgetsUsed, this.renderer);
    }
  }
}
